import React, { useState, useRef, useId, forwardRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { createKeyboardDismissDetector } from '../utils/keyboardDismiss';
import { revealAfterKeyboard } from '../utils/revealBelowSticky';
import PropTypes from 'prop-types';
import { Check, X, ChevronDown, Eye, EyeOff, ArrowRight, Calendar, UserRound, Search, LockKeyhole } from 'lucide-react';
import Button from '../Button';
import './Input.css';
import { MOBILE_QUERY } from '../utils/breakpoints';

/**
 * Input Component
 * 
 * Pixel-perfect implementation from Figma via MCP.
 * Uses Button component for action buttons (cancel, dropdown, submit).
 * 
 * Figma structure:
 * - input (container)
 *   - content (flex row)
 *     - icon (optional left icon)
 *     - label-and-value (flex-1)
 *       - placeholder-label (floating label)
 *       - value (input field)
 *     - right-ui (action buttons)
 *       - success (check icon)
 *       - cancel (Button Tertiary/Text Danger)
 *       - triangle (Button Tertiary/Text - chevron)
 *       - action (Button Tertiary/Black - submit)
 *   - resize (for textarea)
 * - notes (helper text)
 * - error (error tooltip)
 */
const Input = forwardRef(({
  // Core props
  type = 'text',
  label = 'Label',
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  
  // Visual props
  icon = null,
  showIcon = true,           // Show left icon in input
  showOptionIcons = true,    // Show icons in dropdown options
  showSuccess = false,
  showClear = false,
  helperText = '',
  error = '',
  reserveMessageSpace = false, // keep the helper/error line's height even when empty — no layout jump
  disabled = false,
  
  // Action input
  onAction = null,
  actionIcon = null,
  
  // Dropdown props
  options = [],
  onSelect = null,
  multiple = false,
  searchable = false,
  
  // Textarea props
  rows = 4,
  resizable = true,
  
  // Mobile
  mobileFocus = false,       // Enable fullscreen focus mode on mobile (dropdown types, and text inputs when set)
  mobileFocusPlacement = 'top', // 'top' (fullscreen sheet) | 'bottom' (fixed bar above the keyboard, text inputs)
  onMobileFocusChange = null,   // fn(active) — the host can react (e.g. keep a 3D preview above the keyboard)
  mobileFocusReveal = null,     // { section?, sticky?, gap?, align? } — where the host wants the field once the
                                // sheet closes: `section` (closest selector or (wrapper) => Element) is what gets
                                // revealed, `sticky` (document selector or fn) is the element it must sit below.
                                // Without it: the wrapper itself, no sticky, and only when it is off-screen.
  
  // Other props
  className = '',
  id,
  name,
  placeholder,
  autoComplete,
  required = false,
  readOnly = false,
  maxLength,
  minLength,
  pattern,
  onKeyDown,
  ariaLabels: ariaLabelsProp,
  ...props
}, ref) => {
  /* Screen-reader names of the built-in icon buttons — override in the page's language */
  const aria = {
    clear: 'Clear input',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    clearSelection: 'Clear selection',
    toggleDropdown: 'Toggle dropdown',
    openCalendar: 'Open calendar',
    submit: 'Submit',
    remove: 'Remove',
    ...ariaLabelsProp,
  };
  const generatedId = useId();
  const inputId = id || generatedId;
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const blurMobileFocusTimeoutRef = useRef(null);
  const combinedRef = ref || inputRef;
  
  // State
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isMobileFocus, setIsMobileFocus] = useState(false);
  const justClosedRef = useRef(false); // Prevent reopening immediately after close
  const mobileFocusCleanupRef = useRef(null);
  const mobileFocusTimerRef = useRef(null);
  const mobileFocusRafRef = useRef(null);
  const mobileFocusRafNestedRef = useRef(null);
  const mobileFocusRevealCancelRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (mobileFocusTimerRef.current) {
        clearTimeout(mobileFocusTimerRef.current);
        mobileFocusTimerRef.current = null;
      }
      if (mobileFocusRafRef.current) {
        cancelAnimationFrame(mobileFocusRafRef.current);
        mobileFocusRafRef.current = null;
      }
      if (mobileFocusRafNestedRef.current) {
        cancelAnimationFrame(mobileFocusRafNestedRef.current);
        mobileFocusRafNestedRef.current = null;
      }
      // Cleanup mobile focus on unmount (the cleanup arms a reveal — cancel it too)
      mobileFocusCleanupRef.current?.();
      mobileFocusRevealCancelRef.current?.();
      mobileFocusRevealCancelRef.current = null;
    };
  }, []);
  
  // Controlled vs uncontrolled
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const hasValue = currentValue && currentValue.length > 0;
  
  // Determine variant flags
  const isTextarea = type === 'textarea';
  const isPassword = type === 'password';
  const isDropdown = type === 'dropdown' || type === 'select';
  const isAction = type === 'action' || onAction !== null;
  const isDate = type === 'date';
  const isSearchable = type === 'searchable' || searchable;
  const isMultiselect = type === 'multiselect' || multiple;
  const isMobileViewport = typeof window !== 'undefined'
    && window.matchMedia(MOBILE_QUERY).matches;

  // Safari Autofill Polling
  // Safari and some password managers autofill inputs without firing standard React events.
  // We poll the actual DOM element's value to sync it back to React state.
  useEffect(() => {
    if (disabled || isDropdown || isSearchable) return;
    
    const interval = setInterval(() => {
      const el = combinedRef.current;
      if (el && el.value !== currentValue) {
        if (!isControlled) {
          setInternalValue(el.value);
        }
        // Fire standard onChange format
        onChange?.({
          target: el,
          currentTarget: el,
          type: 'change',
          preventDefault: () => {},
          stopPropagation: () => {}
        });
      }
    }, 400);
    
    return () => clearInterval(interval);
  }, [currentValue, isControlled, onChange, combinedRef, disabled, isDropdown, isSearchable]);
  
  // Event handlers
  const handleChange = (e) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(e);
  };

  // Browser autofill detection — Chrome fires a CSS animation on :-webkit-autofill.
  // We detect it via onAnimationStart and sync the DOM value into React state.
  const handleAutoFill = (e) => {
    if (e.animationName === 'onAutoFillStart') {
      const el = combinedRef.current;
      if (el && el.value) {
        if (!isControlled) {
          setInternalValue(el.value);
        }
        onChange?.({ target: el, currentTarget: el });
      }
    }
  };
  const enterMobileFocus = () => {
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    if (!isMobile) return;

    if (mobileFocusTimerRef.current) {
      clearTimeout(mobileFocusTimerRef.current);
      mobileFocusTimerRef.current = null;
    }
    if (mobileFocusRafRef.current) {
      cancelAnimationFrame(mobileFocusRafRef.current);
      mobileFocusRafRef.current = null;
    }
    if (mobileFocusRafNestedRef.current) {
      cancelAnimationFrame(mobileFocusRafNestedRef.current);
      mobileFocusRafNestedRef.current = null;
    }

    // Re-entering the sheet cancels a reveal still pending from the last exit,
    // and the blur-driven exit timer of that previous session — otherwise it
    // fires into the new session and tears the fresh sheet down.
    mobileFocusRevealCancelRef.current?.();
    mobileFocusRevealCancelRef.current = null;
    if (blurMobileFocusTimeoutRef.current) {
      clearTimeout(blurMobileFocusTimeoutRef.current);
      blurMobileFocusTimeoutRef.current = null;
    }

    setIsMobileFocus(true);
    const savedScrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const focusInput = () => {
      combinedRef.current?.focus();
    };

    mobileFocusRafRef.current = requestAnimationFrame(() => {
      // Focus the input so keyboard appears immediately
      if (isSearchable) {
        focusInput();
      }
      mobileFocusRafRef.current = null;

      mobileFocusRafNestedRef.current = requestAnimationFrame(() => {
        if (isSearchable) focusInput();
        mobileFocusRafNestedRef.current = null;
      });
    });

    mobileFocusTimerRef.current = setTimeout(() => {
      if (isSearchable) focusInput();
      mobileFocusTimerRef.current = null;
    }, 50);

    // Keyboard collapsed without a blur (iOS swipe-down, Android back): exit
    // the sheet. The detector handles Chrome's stepwise keyboard animation,
    // where no single resize event is a 100px jump.
    const keyboardDismissed = createKeyboardDismissDetector(
      window.visualViewport ? window.visualViewport.height : window.innerHeight,
    );

    const updateVH = () => {
      const vh = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      const offsetTop = window.visualViewport
        ? window.visualViewport.offsetTop
        : 0;
        
      if (containerRef.current) {
        containerRef.current.style.setProperty('--mobile-vp-height', `${vh}px`);
        containerRef.current.style.setProperty('--mobile-vp-offset', `${offsetTop}px`);
      }

      if (keyboardDismissed(vh)) {
        // Keyboard closed
        if (combinedRef.current) combinedRef.current.blur();
        
        // Wait briefly to allow blur to settle, then guarantee close
        setTimeout(() => {
          exitMobileFocus();
          setIsFocused(false);
          setIsOpen(false);
        }, 100);
      }
    };
    updateVH();
    window.visualViewport?.addEventListener('resize', updateVH);
    window.visualViewport?.addEventListener('scroll', updateVH);

    mobileFocusCleanupRef.current = () => {
      window.visualViewport?.removeEventListener('resize', updateVH);
      window.visualViewport?.removeEventListener('scroll', updateVH);
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;

      // Restore exact scroll position immediately — prevents drift caused by
      // overflow:hidden shifting the browser's scroll offset.
      window.scrollTo({ top: savedScrollY, behavior: 'instant' });

      // Then bring the field to where the host wants it once the keyboard has
      // settled (utils/revealBelowSticky.js: wait for the visual viewport to go
      // quiet, then converge over a few frames). The host describes the target
      // via mobileFocusReveal; the DS never assumes page structure. Default:
      // the wrapper itself, no sticky element, only when it is off-screen.
      const wrapper = containerRef.current;
      const cfg = { gap: 20, align: 'nearest', ...(mobileFocusReveal || {}) };
      const resolver = (spec, viaClosest) => {
        if (typeof spec === 'function') return () => spec(wrapper);
        if (typeof spec === 'string') {
          return () => (viaClosest ? wrapper?.closest(spec) : document.querySelector(spec));
        }
        return null;
      };
      const sectionOf = resolver(cfg.section, true);
      mobileFocusRevealCancelRef.current?.();
      mobileFocusRevealCancelRef.current = revealAfterKeyboard({
        target: () => (sectionOf && sectionOf()) || wrapper,
        sticky: resolver(cfg.sticky, false),
        gap: cfg.gap,
        align: cfg.align,
        onDone: () => { mobileFocusRevealCancelRef.current = null; },
      });

      mobileFocusCleanupRef.current = null;
    };
  };

  const exitMobileFocus = () => {
    if (!isMobileFocus) return;

    if (mobileFocusTimerRef.current) {
      clearTimeout(mobileFocusTimerRef.current);
      mobileFocusTimerRef.current = null;
    }
    if (mobileFocusRafRef.current) {
      cancelAnimationFrame(mobileFocusRafRef.current);
      mobileFocusRafRef.current = null;
    }
    if (mobileFocusRafNestedRef.current) {
      cancelAnimationFrame(mobileFocusRafNestedRef.current);
      mobileFocusRafNestedRef.current = null;
    }

    setIsMobileFocus(false);
    mobileFocusCleanupRef.current?.();
  };

  // Mobile focus sheet must exist BEFORE the browser's default focus action.
  // With the sheet applied only from handleFocus, iOS first focused the input
  // where it stood in the page flow — starting its own scroll-into-view and
  // keyboard pan toward that spot — and THEN the wrapper snapped to
  // position:fixed at the top, so the open read as a jump. Committing the
  // sheet synchronously on pointer-down (flushSync) means the element iOS
  // focuses is already fixed at the visual viewport top: nothing to scroll
  // to, no snap. Because the wrapper moves out from under the finger (and the
  // sheet sets touch-action:none), the tap's own default focus never lands —
  // so the default action is cancelled and focus() is called right here,
  // still inside the user gesture, which is what lets iOS open the keyboard
  // (a focus() from a later rAF/timeout would not). Same recipe as the
  // product page's textfield focus trigger. No-op on desktop
  // (enterMobileFocus checks the viewport).
  const wantsMobileFocusOnFocus = isSearchable
    || (isDropdown && mobileFocus)
    || (mobileFocus && !isDropdown && !isSearchable && !isTextarea);
  const handlePointerDown = (e) => {
    if (wantsMobileFocusOnFocus && !isMobileFocus && !disabled) {
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      if (isMobile) {
        e.preventDefault();
        flushSync(() => enterMobileFocus());
        combinedRef.current?.focus();
      }
    }
    props.onPointerDown?.(e);
  };

  const handleFocus = (e) => {
    // If we just closed after selection, clear the guard and allow this focus to proceed
    if (isSearchable && justClosedRef.current) {
      justClosedRef.current = false;
    }
    setIsFocused(true);
    // Open dropdown on focus only for searchable types (user wants to type and search)
    // Regular dropdown opens on click via handleDropdownToggle
    if (isSearchable && !isOpen) {
      setIsOpen(true);
      // On mobile — enter focus mode (keyboard will appear via enterMobileFocus)
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      if (isMobile) {
        if (!isMobileFocus) enterMobileFocus();
        return;
      }
    } else if (isDropdown && mobileFocus && !isOpen) {
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      if (isMobile) {
        setIsOpen(true);
        if (!isMobileFocus) enterMobileFocus();
        return;
      }
    } else if (mobileFocus && !isDropdown && !isSearchable && !isTextarea) {
      // Plain text input: pin it in a fixed bar sized by the visual viewport so the
      // page is not scrolled under the keyboard (the host keeps its preview visible).
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      if (isMobile && !isMobileFocus) enterMobileFocus();
    }
    onFocus?.(e);
  };

  useEffect(() => {
    onMobileFocusChange?.(isMobileFocus);
  }, [isMobileFocus]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleBlur = (e) => {
    // Don't blur if clicking inside the input container (e.g., dropdown options)
    // This prevents label jump when selecting an option
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }
    // In mobile focus mode, the user might press "Done" on the software keyboard,
    // which triggers a blur. If we just 'return' here, we'd never exit mode.
    // However, tapping an option in the UI also triggers a native blur on iOS Safari!
    // So we apply a small delay—if it was a natural option 'click', `handleOptionSelect` 
    // will execute and clean up correctly. If it was "Done", this timeout effectively closes the mode.
    if (isMobileFocus) {
      blurMobileFocusTimeoutRef.current = setTimeout(() => {
        exitMobileFocus();
        setIsFocused(false);
        setIsOpen(false);
        if (onBlur) onBlur(e);
      }, 150);
      return;
    }
    setIsOpen(false);
    setIsFocused(false);
    onBlur?.(e);
  };
  
  const handleKeyDownInternal = (e) => {
    if (e.key === 'Enter') {
      if (isMobileFocus) {
        exitMobileFocus();
        setIsFocused(false);
        setIsOpen(false);
        combinedRef.current?.blur();
      }
    }
    // Propagate up to parent
    onKeyDown?.(e);
  };
  
  const handleKeyUpInternal = (e) => {
    if (e.key === 'Enter') {
      if (isMobileFocus) {
        exitMobileFocus();
        setIsFocused(false);
        setIsOpen(false);
        combinedRef.current?.blur();
      }
    }
    // Assume input might have onKeyUp passed via props (it's not destructured automatically but it's in ...props)
    if (props.onKeyUp) {
      props.onKeyUp(e);
    }
  };

  const handleClear = (e) => {
    if (blurMobileFocusTimeoutRef.current) {
      clearTimeout(blurMobileFocusTimeoutRef.current);
      blurMobileFocusTimeoutRef.current = null;
    }
    e.stopPropagation();
    if (!isControlled) {
      setInternalValue('');
    }
    onChange?.({ target: { value: '', name } });
    combinedRef.current?.focus();
  };
  
  // Clear for dropdown/searchable - clears selection and notifies via onSelect
  const handleDropdownClear = (e) => {
    if (blurMobileFocusTimeoutRef.current) {
      clearTimeout(blurMobileFocusTimeoutRef.current);
      blurMobileFocusTimeoutRef.current = null;
    }
    e.stopPropagation();
    if (!isControlled) {
      setInternalValue('');
    }
    onChange?.({ target: { value: '', name } });
    onSelect?.(null);

    // If we're in mobile focus mode and it's a searchable input, keep it active
    if (isMobileFocus && isSearchable) {
      combinedRef.current?.focus();
      return;
    }

    // Close dropdown and blur to reset label to default state
    setIsOpen(false);
    setIsFocused(false);
    exitMobileFocus();
    combinedRef.current?.blur();
  };
  
  const handlePasswordToggle = (e) => {
    e.stopPropagation();
    setIsPasswordVisible(!isPasswordVisible);
    combinedRef.current?.focus();
  };
  
  const handleDropdownToggle = (e) => {
    e?.stopPropagation();
    if (!disabled) {
      const opening = !isOpen;
      // Mobile, searchable: the arrow is the same control as the field. It used to
      // open the option list in place — no sheet, no keyboard — while a tap on the
      // field 20px to its left opened the focus sheet. Now the arrow enters the
      // sheet too (focus inside the gesture, so iOS raises the keyboard) and, when
      // the list is open, closes it and leaves the sheet.
      if (isSearchable && window.matchMedia(MOBILE_QUERY).matches) {
        if (opening) {
          flushSync(() => {
            setIsOpen(true);
            setIsFocused(true);
            enterMobileFocus();
          });
          combinedRef.current?.focus();
        } else {
          setIsOpen(false);
          exitMobileFocus();
          setIsFocused(false);
          combinedRef.current?.blur();
        }
        return;
      }
      setIsOpen(opening);
      // For dropdown with mobileFocus, enter/exit fullscreen on toggle
      if (isDropdown && mobileFocus) {
        const isMobile = window.matchMedia(MOBILE_QUERY).matches;
        if (isMobile) {
          if (opening) {
            setIsFocused(true);
            enterMobileFocus();
          } else {
            exitMobileFocus();
            setIsFocused(false);
          }
        }
      }
    }
  };
  
  const handleOptionSelect = (option) => {
    // Clear any pending blur timeouts to prevent anomalous modal closing
    if (blurMobileFocusTimeoutRef.current) {
      clearTimeout(blurMobileFocusTimeoutRef.current);
      blurMobileFocusTimeoutRef.current = null;
    }

    if (isMultiselect) {
      const isSelected = selectedOptions.some(opt => opt.value === option.value);
      const newSelected = isSelected
        ? selectedOptions.filter(opt => opt.value !== option.value)
        : [...selectedOptions, option];
      setSelectedOptions(newSelected);
      onSelect?.(newSelected);
    } else {
      if (!isControlled) {
        setInternalValue(option.label || option.value);
      }
      onChange?.({ target: { value: option.value, name } });
      onSelect?.(option);

      // When in mobile focus mode, defer ALL UI cleanup to the next frame.
      // Options use onMouseDown; the subsequent mouseup + click events must
      // fire on the still-visible option. If we remove the fixed overlay
      // synchronously, the click falls through to whatever is behind
      // (e.g. SchoolSelector overlay) and closes the parent drawer.
      if (isMobileFocus) {
        justClosedRef.current = true;
        requestAnimationFrame(() => {
          setIsOpen(false);
          exitMobileFocus();
          setIsFocused(false);
          combinedRef.current?.blur();
        });
        return;
      }

      setIsOpen(false);
      // For searchable dropdown, prevent immediate reopen and force blur
      if (isSearchable) {
        justClosedRef.current = true;
        setTimeout(() => {
          combinedRef.current?.blur();
          setIsFocused(false);
        }, 0);
      }
      // For dropdown with mobileFocus, also exit fullscreen
      if (isDropdown && mobileFocus) {
        exitMobileFocus();
        setIsFocused(false);
        combinedRef.current?.blur();
      }
    }
  };
  
  const handleTagRemove = (option, e) => {
    e.stopPropagation();
    const newSelected = selectedOptions.filter(opt => opt.value !== option.value);
    setSelectedOptions(newSelected);
    onSelect?.(newSelected);
  };
  
  const handleActionClick = (e) => {
    e.stopPropagation();
    onAction?.(currentValue);
  };
  
  // NOTE (2026-09-07): hover-out no longer closes the dropdown. Moving the
  // mouse away is not an intent to dismiss — it killed keyboard interaction
  // with the checkout school picker (list vanished + focus lost after 150ms).
  // Dismissal stays on blur, outside click, option select and Escape.

  const handleContainerClick = (e) => {
    // Ignore clicks originating from dropdown options area
    if (e?.target?.closest && e.target.closest('.input__dropdown')) {
      return;
    }
    if (!disabled) {
      // User explicitly clicks container: allow reopening
      justClosedRef.current = false;

      const isMobile = window.matchMedia(MOBILE_QUERY).matches;

      // On mobile, searchable inputs always restore/open fullscreen focus mode
      // so repeat opens reliably bring input + keyboard back.
      if (isSearchable && isMobile) {
        if (!isOpen) {
          setIsOpen(true);
        }
        if (!isFocused) {
          setIsFocused(true);
          enterMobileFocus();
        } else if (!isMobileFocus) {
          enterMobileFocus();
        } else {
          combinedRef.current?.focus();
        }
        return;
      }

      // On mobile, dropdown with mobileFocus enters fullscreen
      if (isDropdown && mobileFocus && isMobile) {
        if (!isOpen) {
          setIsOpen(true);
          setIsFocused(true);
          enterMobileFocus();
        }
        return;
      }

      // Regular dropdown: toggle open/close on any container click
      // (clicks on the <input> itself are handled by its own onClick,
      // but clicks on the label/padding only reach handleContainerClick)
      if (isDropdown && !isSearchable) {
        handleDropdownToggle();
        return;
      }

      if (isSearchable && !isOpen) {
        setIsOpen(true);
      }

      combinedRef.current?.focus();
    }
  };
  
  // Build class names
  const wrapperClasses = [
    'input-wrapper',
    error && 'input-wrapper--has-error',
    reserveMessageSpace && 'input-wrapper--reserve-message',
    isOpen && 'input-wrapper--open',
    isMobileFocus && 'input-wrapper--mobile-focus',
    isMobileFocus && mobileFocusPlacement === 'bottom' && 'input-wrapper--mobile-focus-bottom',
    className,
  ].filter(Boolean).join(' ');
  
  const inputClasses = [
    'input',
    isTextarea && 'input--textarea',
    isDropdown && 'input--dropdown',
    isSearchable && 'input--searchable',
    hasValue && 'input--has-value',
    (hasValue && !isFocused) && 'input--filled-out',
    isFocused && 'input--focused',
    error && 'input--error',
    disabled && 'input--disabled',
    isOpen && 'input--open',
  ].filter(Boolean).join(' ');
  
  // Determine actual input type
  const actualInputType = isPassword 
    ? (isPasswordVisible ? 'text' : 'password')
    : (isTextarea ? undefined : (['action', 'dropdown', 'searchable'].includes(type) ? 'text' : type));
  
  // Render left icon
  const renderIcon = () => {
    if (!icon && !showIcon) return null;
    
    // If icon is passed as an element (<UserRound />), use it directly
    // If icon is a component reference (UserRound) or null, render it as JSX
    if (icon && React.isValidElement(icon)) {
      return (
        <div className="input__icon">
          <div className="input__icon-container">
            {icon}
          </div>
        </div>
      );
    }
    
    // Default icon depends on type:
    // - password: LockKeyhole icon (synced from Figma)
    // - searchable: Search icon
    // - others: UserRound or passed icon
    let IconComponent;
    if (icon) {
      IconComponent = icon;
    } else if (isPassword) {
      IconComponent = LockKeyhole;
    } else if (isSearchable) {
      IconComponent = Search;
    } else {
      IconComponent = UserRound;
    }
    
    return (
      <div className="input__icon">
        <div className="input__icon-container">
          <IconComponent size={20} />
        </div>
      </div>
    );
  };
  
  // Render input element
  const renderInputElement = () => {
    const commonProps = {
      id: inputId,
      ref: combinedRef,
      name,
      value: currentValue,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDownInternal,
      onKeyUp: handleKeyUpInternal,
      onAnimationStart: handleAutoFill,
      disabled,
      required,
      readOnly,
      maxLength,
      minLength,
      pattern,
      autoComplete: autoComplete || (isSearchable ? 'off' : undefined),
      placeholder: placeholder || " ",
      className: 'input__field',
      'aria-describedby': error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined,
      'aria-invalid': error ? 'true' : undefined,
      ...props,
      onPointerDown: handlePointerDown,
    };
    
    if (isTextarea) {
      return (
        <textarea
          {...commonProps}
          rows={rows}
          style={{ resize: resizable ? 'vertical' : 'none' }}
        />
      );
    }
    
    return (
      <input
        {...commonProps}
        type={actualInputType}
        readOnly={
          readOnly
          ||
          (isDropdown && !isSearchable)
          || (
            isSearchable
            && hasValue
            && !isFocused
            && !isMobileFocus
            && !(mobileFocus && isMobileViewport)
          )
        }
        onClick={isDropdown ? handleDropdownToggle : undefined}
      />
    );
  };
  
  // Render right-side actions using Button component
  const renderActions = () => {
    const actions = [];
    
    // Success icon (just an icon, not a button)
    if (showSuccess && hasValue && !error) {
      actions.push(
        <div key="success" className="input__success-icon">
          <Check />
        </div>
      );
    }
    
    // Clear button - uses Button Tertiary/Text (black icon, gray hover bg)
    // From Figma: Input populated state with clear button
    if (showClear && hasValue && !disabled) {
      actions.push(
        <div key="clear" className="input__action-btn-wrapper">
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<X size={20} />}
            onClick={handleClear}
            aria-label={aria.clear}
            tabIndex={-1}
          />
        </div>
      );
    }
    
    // Password visibility toggle - uses Button Tertiary/Text
    if (isPassword) {
      actions.push(
        <div key="visibility" className="input__action-btn-wrapper">
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={isPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
            onClick={handlePasswordToggle}
            aria-label={isPasswordVisible ? aria.hidePassword : aria.showPassword}
            tabIndex={-1}
          />
        </div>
      );
    }
    
    // Dropdown chevron - uses Button Tertiary/Text
    if (isDropdown || isSearchable || isMultiselect) {
      // Clear button for dropdown/searchable when value is selected
      if (hasValue && !disabled) {
        actions.push(
          <div key="dropdown-clear" className="input__action-btn-wrapper">
            <Button
              size="tertiary"
              variant="text"
              iconOnly
              iconLeft={<X size={20} />}
              onClick={handleDropdownClear}
              aria-label={aria.clearSelection}
              tabIndex={-1}
            />
          </div>
        );
      }
      actions.push(
        <div key="dropdown" className="input__action-btn-wrapper input__chevron">
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<ChevronDown size={20} />}
            onClick={handleDropdownToggle}
            aria-label={aria.toggleDropdown}
            tabIndex={-1}
          />
        </div>
      );
    }
    
    // Date picker calendar - uses Button Tertiary/Text
    if (isDate) {
      actions.push(
        <div key="calendar" className="input__action-btn-wrapper">
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<Calendar size={20} />}
            onClick={handleDropdownToggle}
            aria-label={aria.openCalendar}
            tabIndex={-1}
          />
        </div>
      );
    }
    
    // Action button - uses Button Tertiary/Black
    if (isAction && onAction) {
      actions.push(
        <div key="action" className="input__action-btn-wrapper">
          <Button
            size="tertiary"
            variant="black"
            iconOnly
            iconLeft={actionIcon || <ArrowRight size={20} />}
            onClick={handleActionClick}
            aria-label={aria.submit}
            disabled={disabled}
          />
        </div>
      );
    }
    
    if (actions.length === 0) return null;
    
    return (
      <div className="input__actions">
        {actions}
      </div>
    );
  };
  
  // Helper function to highlight matching text in options
  // Search text is highlighted in gray-6, rest stays gray-10
  const highlightMatchingText = (text, searchQuery) => {
    if (!searchQuery || !isSearchable) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = searchQuery.toLowerCase().trim();
    
    if (!lowerQuery || !lowerText.includes(lowerQuery)) return text;
    
    const startIndex = lowerText.indexOf(lowerQuery);
    const endIndex = startIndex + lowerQuery.length;
    
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);
    
    return (
      <>
        {before}
        <span className="input__option-text-match">{match}</span>
        {after}
      </>
    );
  };

  // Render dropdown options
  const renderDropdown = () => {
    if (!isDropdown && !isSearchable && !isMultiselect) return null;
    if (options.length === 0) return null;
    
    // Options flagged `fallback` are escape hatches (e.g. "my school is not in
    // the list"): never part of the normal list, rendered only when searching
    // the real options yields nothing.
    const realOptions = options.filter(opt => !opt.fallback);
    const fallbackOptions = options.filter(opt => opt.fallback);

    // For searchable: show all options when dropdown is open but no input yet,
    // filter only when user types something different from selected option
    // Filter options for searchable - only filter when there's actual search text
    // that differs from a selected option label
    const searchText = currentValue || '';
    const isSearchTextMatchingOption = realOptions.some(
      opt => (opt.label || opt.value).toLowerCase() === searchText.toLowerCase()
    );
    const matchedOptions = isSearchable && searchText.trim().length > 0 && !isSearchTextMatchingOption
      ? realOptions.filter(opt =>
          (opt.label || opt.value).toLowerCase().includes(searchText.toLowerCase())
        )
      : realOptions; // Show all options when no search text or text matches selected option

    const filteredOptions = matchedOptions.length > 0 ? matchedOptions : fallbackOptions;
    if (filteredOptions.length === 0) return null;

    // Determine if we should highlight (only when actively searching)
    const shouldHighlight = isSearchable && searchText.trim().length > 0 && !isSearchTextMatchingOption;

    return (
      <div className="input__dropdown" role="listbox">
        <div className="input__dropdown-options">
          {filteredOptions.map((option, index) => {
            const isSelected = isMultiselect
              ? selectedOptions.some(opt => opt.value === option.value)
              : currentValue === (option.label || option.value);
            
            const optionText = option.label || option.value;
            
            return (
              <button
                key={option.value || index}
                type="button"
                className={`input__option ${isSelected ? 'input__option--selected' : ''} ${option.fallback ? 'input__option--fallback' : ''}`}
                onMouseDown={(e) => {
                  // Prevent blur to avoid label flicker when selecting
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionSelect(option);
                }}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
              >
                <div className="input__option-content">
                  {isMultiselect && (
                    <span className="input__option-checkbox">
                      {isSelected && <Check size={14} />}
                    </span>
                  )}
                  {/* Show icon from option or default UserRound - controlled by showOptionIcons */}
                  {showOptionIcons && (
                    <div className="input__option-icon">
                      <div className="input__option-icon-container">
                        {option.icon || <UserRound size={20} />}
                      </div>
                    </div>
                  )}
                  <span className="input__option-text">
                    {shouldHighlight && !option.fallback
                      ? highlightMatchingText(optionText, searchText)
                      : optionText}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  
  // Render multi-select tags
  const renderTags = () => {
    if (!isMultiselect || selectedOptions.length === 0) return null;
    
    return (
      <div className="input__tags">
        {selectedOptions.map((option) => (
          <span key={option.value} className="input__tag">
            {option.label || option.value}
            <button
              type="button"
              className="input__tag-remove"
              onClick={(e) => handleTagRemove(option, e)}
              aria-label={`${aria.remove} ${option.label || option.value}`}
            >
              <X />
            </button>
          </span>
        ))}
      </div>
    );
  };
  
  // Render helper text
  const renderHelper = () => {
    if (error) return null;
    if (!helperText && !reserveMessageSpace) return null;
    
    return (
      <div className="input__helper" id={`${inputId}-helper`}>
        {helperText && <span className="input__helper-text">{helperText}</span>}
      </div>
    );
  };
  
  // Render error tooltip
  const renderError = () => {
    if (!error) return null;
    
    return (
      <div className="input__error" id={`${inputId}-error`} role="alert">
        <span className="input__error-text">{error}</span>
      </div>
    );
  };
  
  // Render resize handle for textarea
  const renderResizeHandle = () => {
    if (!isTextarea || !resizable) return null;
    
    return (
      <div className="input__resize-handle" aria-hidden="true">
        <div className="input__resize-handle-lines">
          {/* SVG lines pattern */}
          <svg viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 1L1 5M5 3.5L3.5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
          </svg>
        </div>
      </div>
    );
  };
  
  return (
    <div 
      className={wrapperClasses} 
      ref={containerRef} 
    >
      <div 
        className={inputClasses}
        onClick={handleContainerClick}
      >
        <div className="input__content">
          {renderIcon()}
          
          <div className="input__field-container">
            <label htmlFor={inputId} className="input__label">
              {label}
            </label>
            
            {isMultiselect && selectedOptions.length > 0 ? (
              renderTags()
            ) : (
              renderInputElement()
            )}
          </div>
          
          {renderActions()}
        </div>
        
        {renderResizeHandle()}
        {renderError()}
      </div>
      
      {renderDropdown()}
      {renderHelper()}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  /** Input type */
  type: PropTypes.oneOf([
    'text',
    'password',
    'textarea',
    'dropdown',
    'select',
    'action',
    'date',
    'searchable',
    'multiselect',
    'email',
    'tel',
    'url',
    'number',
  ]),
  /** Label text (floating) */
  label: PropTypes.string,
  /** Controlled value */
  value: PropTypes.string,
  /** Default value for uncontrolled mode */
  defaultValue: PropTypes.string,
  /** Change handler */
  onChange: PropTypes.func,
  /** Blur handler */
  onBlur: PropTypes.func,
  /** Focus handler */
  onFocus: PropTypes.func,
  /** Custom left icon (React element or component) */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),
  /** Show left icon in input */
  showIcon: PropTypes.bool,
  /** Show icons in dropdown options */
  showOptionIcons: PropTypes.bool,
  /** Show success checkmark when has value */
  showSuccess: PropTypes.bool,
  /** Show clear button when has value */
  showClear: PropTypes.bool,
  /** Helper text below input */
  helperText: PropTypes.string,
  /** Error message */
  error: PropTypes.string,
  /** Keep one message line under the field whether it holds helper text, nothing or the error tooltip — the content below never jumps */
  reserveMessageSpace: PropTypes.bool,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Action button click handler (makes it an action input) */
  onAction: PropTypes.func,
  /** Custom action button icon */
  actionIcon: PropTypes.node,
  /** Dropdown options array. `fallback` marks an escape-hatch option that is
   *  kept out of the normal list and rendered only when the search matches no
   *  other option (e.g. "my school is not in the list"). */
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    icon: PropTypes.node,
    fallback: PropTypes.bool,
  })),
  /** Option select handler */
  onSelect: PropTypes.func,
  /** Enable multi-select mode */
  multiple: PropTypes.bool,
  /** Enable search in dropdown */
  searchable: PropTypes.bool,
  /** Textarea rows */
  rows: PropTypes.number,
  /** Allow textarea resize */
  resizable: PropTypes.bool,
  /** Enable fullscreen focus mode on mobile for dropdown types */
  mobileFocus: PropTypes.bool,
  mobileFocusPlacement: PropTypes.oneOf(['top', 'bottom']),
  onMobileFocusChange: PropTypes.func,
  mobileFocusReveal: PropTypes.shape({
    section: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    sticky: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    gap: PropTypes.number,
    align: PropTypes.oneOf(['top', 'nearest']),
  }),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Input id */
  id: PropTypes.string,
  /** Input name */
  name: PropTypes.string,
  /** Placeholder (not visible due to floating label) */
  placeholder: PropTypes.string,
  /** Autocomplete attribute */
  autoComplete: PropTypes.string,
  /** Required field */
  required: PropTypes.bool,
  /** Native readOnly state */
  readOnly: PropTypes.bool,
  /** Max length */
  maxLength: PropTypes.number,
  /** Min length */
  minLength: PropTypes.number,
  /** Pattern for validation */
  pattern: PropTypes.string,
  /** Screen-reader names of the icon buttons (defaults are English); `remove` prefixes a tag's label */
  ariaLabels: PropTypes.shape({
    clear: PropTypes.string, showPassword: PropTypes.string, hidePassword: PropTypes.string,
    clearSelection: PropTypes.string, toggleDropdown: PropTypes.string, openCalendar: PropTypes.string,
    submit: PropTypes.string, remove: PropTypes.string,
  }),
};

export default Input;
